export type AuthenticateData = {
  sessionId: string;
  username: string;
  userAttributes: UserAttributes;
};

export type UserAttributes = {
  personalNumber: string;
  name: string;
  givenName: string;
  surname: string;
  ipAddress: string;
  notBefore: string;
  notAfter: string;
  signature: string;
  ocspResponse: string;
};

export type GetAuthSessionReturnType = {
  data?: {
    token: string;
  };
  errorMessage?: string;
};

export type AuthenticateReturnType = {
  data?: AuthenticateReturnData;
  errorMessage?: string;
};
export type AuthenticateReturnData = {
  redirectUrl: string;
};

export type PersonalInformation = {
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  city: string;
  apartmentNumber: string;
  housingCooperative: string;
  phoneNumber: string;
};

export type User = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  housingCooperative?: string;
  associationId?: string; // Koppling till associations collection
  role?: string;
};

export type Housing = {
  address: string;
  city: string;
  apartmentNumber: string;
  housingCooperative?: string;
  associationId?: string; // Koppling till associations collection
  phoneNumber?: string;
  zipCode: string;
  ownedBy: string; // UID som kopplar till users collection
  addressVerified?: boolean;
  status?: string; // För unverifiedHousing
  createdAt: string;
  updatedAt: string;
};

export type GetPersonalInformationReturnType = {
  data?: User;
  errorMessage?: string;
};

export type GetHousingInformationReturnType = {
  data?: Housing[];
  errorMessage?: string;
};

export type ChatMessage = {
  id: string;
  text: string;
  senderUid: string;
  senderName: string;
  senderRole: 'owner' | 'brfadmin';
  createdAt: any; // Firestore Timestamp
  attachment?: {
    fileName: string;
    fileUrl: string;
    fileType: string;
    fileSize: number;
    storagePath: string;
  };
};

export type ApplicationData = {
  id?: string;
  reason: string;
  address: string;
  city: string;
  zipCode: string;
  fromDate: string;
  brfEmail?: string;
  toDate: string;
  createdBy: string;
  apartmentNumber: string;
  housingCooperative: string;
  associationId?: string; // Koppling till associations collection
  tenantAccepted: boolean;
  status: ApplicationStatus;
  step: 1 | 2 | 3;
  squareMeter: number;
  rooms: number;

  // CHATT - Denormaliserat för snabb visning i listor
  chatUnread?: { [uid: string]: number };
  chatLastMessage?: { text: string; senderName: string; createdAt: string };
  
  // HYRESGÄSTINFORMATION - Optional (fylls i steg 2)
  tenantFirstName?: string;
  tenantLastName?: string;
  tenantPhoneNumber?: string;
  tenantPersonalNumber?: string;
  tenantEmail?: string;
  
  // AVTALSPERIOD - Optional (fylls i steg 2, inom uthyrningsperioden från steg 1)
  contractStartDate?: string;
  contractEndDate?: string;
  
  // EKONOMI - Optional (fylls i steg 2)
  rentalFee?: number;
  deposit?: number;
  clearingnummer?: string;
  bankAccountNumber?: string;
  
  // TJÄNSTER - Optional (fylls i steg 2)
  heatingIncluded?: boolean;
  hotWaterIncluded?: boolean;
  electricityIncluded?: boolean;
  internetIncluded?: boolean;
  parkingIncluded?: boolean;
  furnitureIncluded?: boolean;
  
  // ÖVRIGT - Optional (fylls i steg 2)
  miscellaneousNotes?: string;

  // KREDITKONTROLL - Sub-status (ny struktur för förbättrat notifieringsflöde)
  creditCheck?: {
    status?: 'consent_pending' | 'consent_received' | 'report_uploaded' | null;
    consentDate?: string;
    consentGiven?: boolean;
    consentToken?: string;
    reportUrl?: string;
    uploadedDate?: string;
    uploadedBy?: string;
  };

  // AVTALSFLÖDE - Sub-status (ny struktur för förbättrat notifieringsflöde)
  contractFlow?: {
    status?: 'pending_owner_decision' | 'contract_requested' | 'contract_sent' | null;
    ownerDecision?: 'create_contract' | 'reject' | null;
    decisionDate?: string;
    requestedDate?: string;
    contractUrl?: string;
    sentDate?: string;
  };

  // BILAGOR - Bifogade dokument
  attachments?: AttachmentItem[];

  // UTHYRNINGSSTATUS - Livscykelstatus för uthyrningen (baserat på avtalstid)
  rentalStatus?: RentalStatus;
  rentalStatusLastUpdated?: string;

  // FÖRLÄNGNINGSFÖRFRÅGAN - För att hantera förlängning av befintlig uthyrning
  extensionRequest?: {
    newEndDate: string;
    status: 'pending' | 'approved' | 'rejected';
    requestedDate: string;
    requestedBy: string;
    processedDate?: string;
    processedBy?: string;
    rejectionReason?: string;
  };

  // UPPSÄGNING - För att hantera uppsägning av uthyrning
  terminationRequest?: {
    status: 'pending_tenant_signature' | 'signed';
    moveOutDate: string;
    reason?: string;
    signingToken: string;
    initiatedDate: string;
    initiatedBy: string;
    ownerSignedDate: string;
    tenantSignedDate?: string;
  };
};

export type GetApplicationsReturnType = {
  data?: ApplicationData[];
  errorMessage?: string;
};

export enum ApplicationStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  ADDITIONAL_INFO = 'ADDITIONAL_INFO',
  TENANT_INFO_SUBMITTED = 'TENANT_INFO_SUBMITTED',
  CREDIT_CHECK_COMPLETE = 'CREDIT_CHECK_COMPLETE',
  AWAITING_OWNER_DECISION = 'AWAITING_OWNER_DECISION',
  FINAL_APPROVED = 'FINAL_APPROVED',
  OWNER_REJECTED = 'OWNER_REJECTED',
  FINAL_REJECTED = 'FINAL_REJECTED'
}

export type RentalStatus = 'APPROVED' | 'ACTIVE' | 'EXPIRING_SOON' | 'COMPLETED' | 'TERMINATING';

export type AttachmentItem = {
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  storagePath: string;
  uploadedAt: string;
  uploadedBy: string;
  uploadedByName: string;
  category: 'hyresavtal' | 'kreditupplysning' | 'övrigt';
};

export type BackgroundCheck = {
  applicationId: string;
  inkomstOk: boolean;
  kronofogden: boolean;
  litigation: boolean;
  paymentRemarks: boolean;
  prosecuted: boolean;
};
